import { useState } from "react";
import * as Icons from "lucide-react";
import { PageHeader, GlassCard, EmptyState, useCrud, ItemDialog, RowActions } from "../_shared";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

const ICON_HINT = "Any lucide-react icon name (e.g. Sparkles, Zap, Wallet, ShieldAlert, FileCheck, RefreshCcw, Truck, Landmark).";

const FIELDS = [
  { key: "title", label: "Title", required: true },
  { key: "desc", label: "Description", required: true, type: "textarea", rows: 3 },
  { key: "icon", label: "Icon", placeholder: "Sparkles", help: ICON_HINT },
  { key: "key", label: "URL key (unique short id)", required: true, placeholder: "new-fastag" },
  { key: "enabled", label: "Visible on site", type: "switch" },
];

export default function ServicesManager() {
  const { items, loading, busy, create, update, remove, move } = useCrud("/admin/cms/services");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing({ enabled: true, icon: "Sparkles" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setOpen(true); };
  const submit = async (form) => {
    if (editing?.id) await update(editing.id, form); else await create(form);
    setOpen(false);
  };

  return (
    <>
      <PageHeader
        title="Services Manager"
        subtitle="Cards shown in the Services grid on the homepage and /services page."
        actions={<Button onClick={openNew} className="bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full" data-testid="new-service-btn"><Plus className="w-4 h-4 mr-1" /> New Service</Button>}
      />
      {loading ? <div className="text-slate-500">Loading…</div> : items.length === 0 ? (
        <EmptyState label="No services yet." action={<Button onClick={openNew}>Add first service</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((it, i) => {
            const Ic = Icons[it.icon] || Icons.Sparkles;
            return (
              <GlassCard key={it.id} className="p-4">
                <div className="flex gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#0057B8]/10 text-[#0057B8] grid place-items-center shrink-0">
                    <Ic className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-display font-semibold text-slate-900 truncate">{it.title}</div>
                      {!it.enabled && <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Hidden</span>}
                    </div>
                    <div className="text-sm text-slate-500 line-clamp-2 mt-0.5">{it.desc}</div>
                    <div className="text-xs text-slate-400 mt-1 font-mono">{it.key}</div>
                  </div>
                  <RowActions
                    onEdit={() => openEdit(it)} onDelete={() => remove(it.id)}
                    onUp={() => move(it.id, "up")} onDown={() => move(it.id, "down")}
                    canUp={i > 0} canDown={i < items.length - 1}
                  />
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
      <ItemDialog
        open={open} onOpenChange={setOpen}
        title={editing?.id ? "Edit Service" : "New Service"}
        fields={FIELDS}
        initial={editing || { enabled: true, icon: "Sparkles" }}
        onSubmit={submit}
        submitting={busy}
      />
    </>
  );
}
